import { DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewEncapsulation, inject, input, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ErrorMessage } from '@app/common/services/error.service';
import { TabHeaderComponent } from '@app/shared/components/tab-header/tab-header.component';
import { ProjectResourceItemType } from '@app/shared/data/project-enums';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { hasClass, isChildOf } from '@app/shared/utilities/javascript-utilities';
import { ButtonDirective, ButtonGroupComponent } from '@progress/kendo-angular-buttons';
import { DialogActionsComponent, DialogComponent } from '@progress/kendo-angular-dialog';
import { EditEvent, GridComponent, GridDataResult, GridModule, RowArgs, RowClassArgs, SelectionEvent } from '@progress/kendo-angular-grid';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { PopupComponent } from '@progress/kendo-angular-popup';
import { State, process } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { SelectedTab } from '../../../../shared/models/selected-tab';
import { ItemCustom } from '../../models/item-custom';
import { ProjectResourceItem } from '../../models/project-resource-item';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { ProjectCustomEndpointService } from '../../services/project-custom-endpoint.service';
import { ProjectEstimateService } from '../../services/project-estimate.service';
import { ProjectResourceGridService } from '../../services/project-resource-grid.service';
import { StandardConfigSyncComponent } from '../../standard-config-sync/standard-config-sync.component';
import { ProjectItemSelectComponent } from '../project-item-select/project-item-select.component';

@Component({
  selector: 'app-project-resource-items',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './project-resource-items.component.html',
  styleUrls: ['./project-resource-items.component.css'],
  host: {
    '(click)': 'onClick($event)',
  },
  imports: [
    ButtonDirective,
    ButtonGroupComponent,
    DatePipe,
    DialogActionsComponent,
    DialogComponent,
    GridModule,
    LoaderComponent,
    PopupComponent,
    ProjectItemSelectComponent,
    StandardConfigSyncComponent,
    TabHeaderComponent,
  ],
  providers: [
    ProjectResourceGridService,
  ],
})
export class ProjectResourceItemsComponent implements OnInit, AfterViewInit {
  private lookupPlusService = inject(LookupPlusService);
  private projectCustomEndpointService = inject(ProjectCustomEndpointService);
  private formBuilder = inject(UntypedFormBuilder);
  private projectResourceGridService = inject(ProjectResourceGridService);
  private projectEstimateService = inject(ProjectEstimateService);
  private cdr = inject(ChangeDetectorRef);

  readonly standardConfigSyncComponent = viewChild(StandardConfigSyncComponent);
  private readonly grid = viewChild(GridComponent);

  onClick(e: MouseEvent) {
    if (!isChildOf(e.target, 'k-grid-content') && !hasClass(e.target, 'k-button')) {
      this.saveRow();
    }
  }

  private tProjectResourceItem: string = 'ProjectResourceItems';
  private oProjectResourceItem: string = '$expand=Item(%24expand%3DUnitOfMeasure)%2C%20UnitOfMeasure%2C%20CostCodeCostType(%24expand%3DCostCode%2C%20CostType)';
  private fProjectResourceItem: string = '&$filter=ProjectId%20eq%20';
  private sortProjectResourceItem: string = `&$orderby=IsManualAdd%20desc%2C%20Phase%2C%20Step%2C%20ItemNumber`;

  private urlProjectResourceItem: string;
  private editedRowIndex: number;

  public icons = kendoIcons;
  public selectedTab: SelectedTab;
  public dProjectResourceItem: GridDataResult;
  public dViewProjectResourceItem = Array<ProjectResourceItem>();

  public dManualProjectResourceItem = Array<ProjectResourceItem>();
  public newProjectResourceItem: ProjectResourceItem;
  public hasManuallyEnteredItems: boolean;
  public itemsHaveBeenAdded: boolean;
  public isInEditMode: boolean;
  public editButtonLabel: string;
  public primaryButtonColor: boolean;
  public initialState: boolean;
  public updatingGrid: boolean = true;
  public anchorElement: ElementRef;
  public itemToDelete: any = {};
  public showDeletePopup: boolean;
  public anchorAlign: any = { horizontal: 'center', vertical: 'center' };
  public popupAlign: any = { horizontal: 'center', vertical: 'center' };
  public isItemsDialogOpen: boolean = false;

  public selectedKeys: Array<number> = [];
  public selectedItems: Array<RowArgs> = [];
  public editedDataItem: any;
  public editing: boolean = false;
  public displayRemoveItemDialog: boolean = false;
  public formGroup: UntypedFormGroup;
  public view: Observable<GridDataResult>;
  public state: State = {
    sort: [],
    skip: 0,
  };

  readonly iProjectId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);

  constructor() {
    this.view = this.projectResourceGridService;
  }

  ngOnInit() {
    this.urlProjectResourceItem = `${this.oProjectResourceItem}${this.fProjectResourceItem}${this.iProjectId()}${this.sortProjectResourceItem}`;
    this.getProjectResourceItems(this.tProjectResourceItem, this.urlProjectResourceItem);
    this.hasManuallyEnteredItems = false;
    this.itemsHaveBeenAdded = false;
    this.dManualProjectResourceItem = [];
    this.isInEditMode = false;
    this.primaryButtonColor = true;
    this.editButtonLabel = 'Edit Items';
    this.initialState = true;
    this.showDeletePopup = false;
  }

  ngAfterViewInit(): void {
    this.refreshStandardConfiguration();
  }

  public addButtonData: Array<any> = [
    {
      text: 'Items',
      click: () => { this.isItemsDialogOpen = true; }
    }
  ];

  private loadProducts(): void {
    this.getProjectResourceItems(this.tProjectResourceItem, this.urlProjectResourceItem);
  }

  public refreshStandardConfiguration(): void {
    this.standardConfigSyncComponent().refreshStandardConfiguration();
  }

  public onStateChange(state: State) {
    this.state = state;

    this.dProjectResourceItem = process(this.dViewProjectResourceItem, this.state);
  }

  public selectionChange(event: SelectionEvent) {
    event.selectedRows.forEach((row) => {
      this.selectedItems.push(row);
    });

    event.deselectedRows.forEach((row) => {
      this.selectedItems = this.selectedItems.filter(f => f.index !== row.index);
    });

    if (this.editing) {
      this.clearSelections();
    }
  }

  public deleteItem() {
    // todo: add popup to ask for confirmation of the delete
    this
      .projectResourceGridService
      .remove(this.itemToDelete)
      .then(() =>
        this.getProjectResourceItems(this.tProjectResourceItem, this.urlProjectResourceItem));
    this.showDeletePopup = false;
  }

  public cancelDeleteItem() {
    this.itemToDelete = {};
    this.showDeletePopup = false;
  }

  public removeHandler({ dataItem }) {
    this.itemToDelete = dataItem;
    this.showDeletePopup = true;
    // todo: add popup to ask for confirmation of the delete
  }

  public editRow({ dataItem, rowIndex, isEdited, column, columnIndex }: any): void {
    if (columnIndex === 0 && column.isCheckboxColumn) {
      return;
    }

    if (!dataItem.IsManualAdd) {
      return;
    }

    this.editHandler({
      dataItem: dataItem,
      rowIndex: rowIndex,
      sender: this.grid(),
      isNew: false
    });
  }

  public editHandler({ sender, rowIndex, dataItem }: EditEvent): void {
    if (this.formGroup && !this.formGroup.valid) {
      return;
    }

    this.saveRow();
    this.editedDataItem = dataItem;

    this.formGroup = this.createFormGroup(dataItem);

    this.editedRowIndex = rowIndex;
    this.editing = true;
    sender.editRow(rowIndex, this.formGroup);
  }

  public saveRow(): void {
    if (this.formGroup != null && this.formGroup.valid && this.formGroup.dirty) {
      this.projectResourceGridService.assignValues(this.editedDataItem, this.formGroup.value);
      let p = this.projectResourceGridService.update(this.editedDataItem);

      p.then((response) => {
        this.getProjectResourceItems(this.tProjectResourceItem, this.urlProjectResourceItem);
      });
    }

    this.closeEditor();
  }

  public closeEditor(rowIndex: number = this.editedRowIndex) {
    this.editing = false;

    this.grid().closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;

    this.clearSelections();
  }

  public clearSelections() {
    this.selectedItems = [];
    this.selectedKeys = [];
  }

  public removeItems() {
    let allowed: boolean = true;

    this.selectedItems.forEach((row) => {
      if (!row.dataItem.IsManualAdd) {
        allowed = false;
      }
    });

    if (allowed) {
      this.displayRemoveItemDialog = true;
    }
  }

  public acceptRemoveItems() {
    let promises: Array<Promise<Object>> = [];

    this.updatingGrid = true;

    this.selectedItems.forEach((row) => {
      let p: Promise<Object>;

      if (row.dataItem.IsManualAdd) {
        p = this.projectResourceGridService.remove(row.dataItem);
      }

      if (p != null) {
        promises.push(p);
      }
    });

    this.displayRemoveItemDialog = false;

    if (promises.length === 0) {
      this.updatingGrid = false;
      return;
    }

    Promise.all(promises).then((response) => {
      this.clearSelections();
      this.getProjectResourceItems(this.tProjectResourceItem, this.urlProjectResourceItem);
    });
  }

  public declineRemoveItems() {
    this.displayRemoveItemDialog = false;

    this.clearSelections();
  }

  public createFormGroup(dataItem: any): UntypedFormGroup {
    return this.formBuilder.group({

      'Quantity': new UntypedFormControl(dataItem.Quantity)
    });
  }

  public getProjectResourceItems(table: string, oString: string) {
    this.lookupPlusService.getLookup(table, oString)
      .then(payload => {
        let results = payload.value.map(m => m);

        results.forEach(element => {
          const costCode = element.CostCodeCostType?.CostCode;
          if (element.IsManualAdd === true) {
            this.hasManuallyEnteredItems = true;
          }

          element.typeDesc = element.IsManualAdd ? 'Manual Item' : 'Calculated Item';
          element.GFCM = [costCode?.CostCodeValue, costCode?.CostCodeDescription]
            .filter(value => value != null && value !== '')
            .join(' - ');
        });

        this.dProjectResourceItem = process(results, this.state);
        this.dViewProjectResourceItem = results;

        this.updatingGrid = false;
        this.cdr.detectChanges();
      });
  }

  public saveNewItem(pri: { items: Array<ItemCustom>, phaseSteps: string[] }): void {
    let urlSave: string = 'ProjectResourceItems';
    let promises: Promise<Object | ErrorMessage>[] = [];

    pri.items.forEach(e => {
      let x: ProjectResourceItem = {
        ItemNumber: e.ItemNumber,
        ItemId: e.ItemId,
        IsManualAdd: true,
        ProjectResourceItemTypeId: ProjectResourceItemType.ManualItem,
        Quantity: 0,
        Rate: 0,
        ItemDescription: `Manual Add:${e.ItemDescription}`,
        ProjectId: this.iProjectId(),
        ProjectResourceItemId: 0,
        ItemCategoryId: e.ItemCategoryId,
        CostCodeCostTypeId: e.CostCodeCostTypeId,
        UnitOfMeasureId: e.UnitOfMeasureId ? e.UnitOfMeasureId : 1,
        Phase: '',
        Step: ''
      };

      let p = this.projectCustomEndpointService.create(urlSave, x);
      promises.push(p);
    });

    this.isItemsDialogOpen = false;
    this.updatingGrid = true;

    Promise.all(promises).then(response => {
      this.getProjectResourceItems(this.tProjectResourceItem, this.urlProjectResourceItem);
    });
  }

  public onStdConfigChange(e): void {
    this.updatingGrid = true;
    this.projectEstimateService.startEstimateEngine(this.iProjectId(), 1).then(x => {
      this.loadProducts();
    });
  }

  public closeDialog() {
    this.isItemsDialogOpen = false;
  }

  public toggleEdit(): void {
    if (this.initialState) {
      this.isInEditMode = true;
      this.primaryButtonColor = false;
      this.editButtonLabel = 'Exit Editing';
      this.initialState = false;
      return;
    }

    if (this.isInEditMode) {
      this.isInEditMode = false;
      this.primaryButtonColor = true;
      this.editButtonLabel = 'Edit Items';
    } else {
      this.isInEditMode = true;
      this.primaryButtonColor = false;
      this.editButtonLabel = 'Exit Editing';
    }
  }

  public rowCallback(context: RowClassArgs) {
    return {
      active: context.dataItem.IsManualAdd
    };
  }
}